import { CheckboxField, Divider, UploadArea, FilesList, Flex, Heading, Link, Text } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { UPLOAD_HELPER_MESSAGES, getStageCompletionStatusQueryOptions } from "shared-ui";
import { handleContactTeam, uploadManagerHookFactory } from "shared-utils";
import { useCustomerAppConfig } from "undercarriage";

import { DOCUMENT_TYPE_UPLOADS_API, VALIDATED_DOCUMENT_TYPE_UPLOADS_API } from "../../../components/constants";
import { useDocumentQuery } from "../../../queries/document-uploads/queries";
import { useValidatedDocumentQuery } from "../../../queries/document-uploads/validated-documents-queries";
import { useFundingApplication } from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";

import { MESSAGES } from "./message-definitions";

export interface DocumentUploadProps {
  documentType: UploadDocumentType;
  description: React.ReactNode;
  skipped?: boolean | null;
  onSkipCheckbox?: (checked: boolean) => void;
  skipCheckboxDescription?: string;
  header?: string;
  fieldLabel?: string;
  supportedFileExtensions?: string[] /* just a FE hint, no checking done */;
  fileSizeLimitMB?: number;
  slot?: React.ReactNode;
  disabled?: boolean;
}

export const DocumentUpload = ({
  header,
  description,
  fieldLabel,
  documentType,
  supportedFileExtensions,
  fileSizeLimitMB,
  skipped,
  onSkipCheckbox,
  skipCheckboxDescription,
  slot = null,
  disabled,
}: DocumentUploadProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const {
    data: { id: applicationId },
  } = useFundingApplication(customerDomain);

  const isValidatedDocumentType =
    documentType === UploadDocumentType.PROFIT_AND_LOSS || documentType === UploadDocumentType.BALANCE_SHEET;

  const baseUrlPath = (isValidatedDocumentType ? VALIDATED_DOCUMENT_TYPE_UPLOADS_API : DOCUMENT_TYPE_UPLOADS_API)
    .replace(":customerSlug", customerDomain)
    .replace(":applicationId", applicationId.toString())
    .replace(":documentType", documentType);

  const createUrlPath = `${baseUrlPath}/upload`;
  const confirmUploadPath = `${baseUrlPath}/confirm`;
  const deleteUploadPath = `${baseUrlPath}/delete`;

  const useDocumentListQuery = isValidatedDocumentType ? useValidatedDocumentQuery : useDocumentQuery;

  const { data, refetch, isLoading } = useDocumentListQuery(customerDomain, applicationId);
  const responseForType = data?.find((resp) => resp.documentType === documentType);
  const files = responseForType?.data ?? [];

  const useUploadManager = uploadManagerHookFactory<FundingApplicationSchemas.File>(
    {
      files,
      createUrlPath,
      confirmUploadPath,
      deleteUploadPath,
      supportedFileExtensions,
      sizeLimitMB: fileSizeLimitMB,
      onUploadSuccess: () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(customerDomain).queryKey });
      },
      onRemoveComplete: () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(customerDomain).queryKey });
      },
    },
    intl
  );

  const {
    uploadsState,
    onFilesAdded,
    onFileRemoved,
    sizeLimitMB,
    supportedFileExtensions: supportedExtensions,
    onFilesChanged,
  } = useUploadManager();

  React.useEffect(() => {
    if (!isLoading) {
      onFilesChanged();
    }
  }, [isLoading, onFilesChanged]);

  // Generate helperText from upload constraints
  const helperText = useMemo(() => {
    let text = "";

    if (sizeLimitMB) {
      text += intl.formatMessage(UPLOAD_HELPER_MESSAGES.uploadFileSizeLimit, { sizeLimitMb: sizeLimitMB });
    }

    if (supportedExtensions && supportedExtensions.length > 0) {
      text +=
        " " +
        intl.formatMessage(UPLOAD_HELPER_MESSAGES.supportedFileExtensionUpload, {
          supportedFileExtensions: supportedExtensions.join(", "),
        });
    }

    return text.trim();
  }, [sizeLimitMB, supportedExtensions, intl]);

  return (
    <Flex direction="column" gap="4">
      {header && <Heading>{header}</Heading>}
      {description}
      {slot}
      <UploadArea
        label={fieldLabel}
        onFilesAdded={onFilesAdded}
        sizeLimitMB={sizeLimitMB}
        helperText={helperText}
        disabled={disabled}
        multiple
      />
      {uploadsState && uploadsState.uploads.length > 0 ? (
        <FilesList uploadsState={uploadsState} onFileRemoved={onFileRemoved} />
      ) : null}
      {onSkipCheckbox ? (
        <Flex direction="column" gap="4">
          <Divider label={intl.formatMessage(MESSAGES.skipCheckboxDividerLabel)} />
          <CheckboxField
            name="skip-document-upload-field"
            options={{
              name: "skip_upload",
              label: skipCheckboxDescription ?? intl.formatMessage(MESSAGES.skipCheckboxLabel),
              // HACK: See https://github.com/wayflyer/wayflyer/pull/21846
              checked: skipped as boolean | undefined,
              onChange: (checked) => {
                onSkipCheckbox(checked);
              },
            }}
          />
        </Flex>
      ) : null}

      <Text size="xs">
        {intl.formatMessage(MESSAGES.havingTroubleFooterText, {
          link: (str: string) => <Link onClick={handleContactTeam}>{str}</Link>,
        })}
      </Text>
    </Flex>
  );
};
